/**
 * UCC Compliance Hooks
 *
 * React Query hooks for filing audit trail and compliance reporting.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import type { operations } from '@aaimplatform/institutional-types/ucc-filing-audit-compliance';
import { uccFilingAuditComplianceClient } from '../api/typed-clients';
import { apiErrorMessage } from '../api/error-message';

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    throw new Error(apiErrorMessage(errorBody) ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

export function useAuditEvents(params: { tenantId: string; filingId?: string; eventType?: string; from?: string; to?: string; cursor?: string; limit?: number }) {
  return useQuery({
    queryKey: ['uccAuditEvents', params],
    queryFn: async () => {
      const result = await uccFilingAuditComplianceClient.GET('/filing-audit-events', {
        params: {
          query: {
            tenantId: params.tenantId,
            filingId: params.filingId,
            eventType: params.eventType,
            from: params.from,
            to: params.to,
            cursor: params.cursor,
            limit: params.limit,
          },
        },
      });
      return unwrap(result);
    },
    enabled: !!params.tenantId,
  });
}

export function useGenerateComplianceReport() {
  return useMutation({
    // POST /compliance-reports/generate declares its body inline in the spec, so
    // it is read from the generated operation rather than a components schema.
    mutationFn: async (
      body: operations['generateComplianceReport']['requestBody']['content']['application/json'],
    ) => {
      const result = await uccFilingAuditComplianceClient.POST('/compliance-reports/generate', { body });
      return unwrap(result);
    },
  });
}

export function useComplianceReport(reportId: string | undefined) {
  return useQuery({
    queryKey: ['uccComplianceReport', reportId],
    queryFn: async () => {
      const result = await uccFilingAuditComplianceClient.GET('/compliance-reports/{reportId}', {
        params: { query: { tenantId: 'default' }, path: { reportId: reportId! } },
      });
      return unwrap(result);
    },
    enabled: !!reportId,
  });
}
